// Layout.jsx
import Navbar from "./Navbar";
import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const Layout = ({ children }) => {
  const [showNavbar, setShowNavbar] = useState(true);
  const [isNavbarAnimating, setIsNavbarAnimating] = useState(false);
  const lastScrollY = useRef(0);
  const headerRef = useRef(null);
  const [headerActualHeight, setHeaderActualHeight] = useState(0);

  const location = useLocation();
  const navigate = useNavigate();
  const isOperatorDemoPage = location.pathname.startsWith("/demo/");
  const applyScrollHide = location.pathname.includes("/search");

  useEffect(() => {
    if (isOperatorDemoPage) {
      setHeaderActualHeight(0);
      return;
    }

    if (headerRef.current) {
      setHeaderActualHeight(headerRef.current.offsetHeight);
    }
  }, [isOperatorDemoPage]);

  // ✅ Global session-expired handling (when refresh fails)
  useEffect(() => {
    const handleSessionLogout = () => {
      try {
        toast.error("Session expired. Please login again.");
      } catch {}

      // Avoid redirect loop if already on login
      if (location.pathname === "/login") return;

      const current =
        (location.pathname || "/") +
        (location.search || "") +
        (location.hash || "");

      navigate(`/login?redirect=${encodeURIComponent(current)}`);
    };

    window.addEventListener("rb:logout", handleSessionLogout);
    return () =>
      window.removeEventListener("rb:logout", handleSessionLogout);
  }, [navigate, location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (isOperatorDemoPage) {
      setShowNavbar(false);
      return;
    }

    if (!applyScrollHide) {
      setShowNavbar(true); // Ensure Navbar is always visible on other pages
      return;
    }

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const hideThreshold = 250; // Increased threshold: Hide Navbar after scrolling down this far
      const showThreshold = 50; // Show Navbar when scrolling up to this point (closer to top)

      // Scrolling down
      if (
        currentScrollY > lastScrollY.current &&
        currentScrollY > hideThreshold
      ) {
        setShowNavbar(false);
      }
      // Scrolling up AND current scroll position is above the show threshold
      else if (
        currentScrollY < lastScrollY.current &&
        currentScrollY <= showThreshold
      ) {
        setShowNavbar(true);
      }
      // If at the very top of the page, ensure Navbar is visible
      else if (currentScrollY === 0) {
        setShowNavbar(true);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [applyScrollHide, isOperatorDemoPage]);

  // Effect to track Navbar animation state (remains the same)
  useEffect(() => {
    let timeoutId;
    if (applyScrollHide && !isOperatorDemoPage) {
      setIsNavbarAnimating(true);
      timeoutId = setTimeout(() => {
        setIsNavbarAnimating(false);
      }, 300); // Match this with your CSS transition duration (0.3s)
    } else {
      setIsNavbarAnimating(false);
    }
    return () => clearTimeout(timeoutId);
  }, [showNavbar, applyScrollHide, isOperatorDemoPage]);

  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child) && child.type.name === "SearchResults") {
      return React.cloneElement(child, {
        showNavbar,
        headerHeight: headerActualHeight,
        isNavbarAnimating,
      });
    }
    return child;
  });

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FFFFFF" }}>
      {!isOperatorDemoPage && (
        <header
          ref={headerRef}
          className={`w-full z-50 transition-transform duration-300 ease-in-out`}
          style={{
            backgroundColor: "#FFFFFF",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            boxShadow: showNavbar ? "0 2px 4px rgba(0,0,0,0.08)" : "none",
            transform: showNavbar ? "translateY(0)" : "translateY(-100%)",
          }}
        >
          <Navbar />
        </header>
      )}

      <main style={{ paddingTop: isOperatorDemoPage ? 0 : `${headerActualHeight}px` }}>
        {childrenWithProps}
      </main>
    </div>
  );
};

export default Layout;
